import { useState, useRef, useEffect } from "react";
import { categoryList } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
        setDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const closeAll = () => { setMobileMenuOpen(false); setDropdownOpen(false); };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
          : "bg-background-50"
      }`}
      ref={mobileRef}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[68px]">
          <a href="/" className="flex items-center gap-2 flex-shrink-0" onClick={closeAll}>
            <span className="text-xl md:text-2xl font-heading font-extrabold text-foreground-950 whitespace-nowrap">
              pandabuy<span className="text-primary-500">.guru</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-6">
            <a href="/" className="text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap">Home</a>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap"
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[580px] bg-background-50 rounded-2xl border border-background-200/70 shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden animate-[fadeIn_0.2s_ease-out]">
                  <div className="grid grid-cols-2 gap-1 p-3 max-h-[420px] overflow-y-auto">
                    {categoryList.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-primary-50 transition-colors duration-150 group"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50 group-hover:bg-primary-100 transition-colors">
                          <span className="text-[10px] font-bold text-primary-600">{cat.name.charAt(0)}</span>
                        </div>
                        <span className="text-xs font-semibold text-foreground-800 group-hover:text-foreground-950 whitespace-nowrap">
                          {cat.name}
                        </span>
                        <span className="text-[10px] text-foreground-400 ml-auto whitespace-nowrap">
                          {cat.updateFrequency}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a href="/article/pandabuy-spreadsheet-guide" className="text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap">Shopping Guide</a>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center justify-center px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 border-2 border-foreground-900 text-foreground-900 hover:bg-foreground-900 hover:text-background-50"
            >
              Pandabuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 bg-primary-500 text-background-50 hover:bg-primary-600 hover:translate-y-[-1px]"
            >
              Buy It Now
            </a>

            <button
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl bg-background-100 text-foreground-800 hover:bg-background-200 transition-colors"
              onClick={() => { setMobileMenuOpen(!mobileMenuOpen); if (dropdownOpen) setDropdownOpen(false); }}
            >
              <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 pb-6 pt-3 animate-[fadeIn_0.2s_ease-out]">
            <a href="/" className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-xl transition-colors" onClick={closeAll}>
              <i className="ri-home-4-line text-primary-500"></i> Home
            </a>
            <a href="/article/pandabuy-spreadsheet-guide" className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-xl transition-colors" onClick={closeAll}>
              <i className="ri-book-open-line text-primary-500"></i> Shopping Guide
            </a>
            <div className="mx-4 my-3 border-t border-background-200/60"></div>
            <p className="px-4 py-2 text-[11px] font-semibold text-foreground-400 uppercase tracking-wider">All Categories</p>
            <div className="grid grid-cols-2 gap-1 px-2 mt-1">
              {categoryList.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-2 py-2.5 text-xs font-medium text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors"
                  onClick={closeAll}
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-50 text-[10px] font-bold text-primary-600">{cat.name.charAt(0)}</span>
                  <span className="whitespace-nowrap">{cat.name}</span>
                </a>
              ))}
            </div>
            <div className="mx-4 my-3 border-t border-background-200/60"></div>
            <div className="flex items-center gap-3 px-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 text-center px-4 py-2.5 rounded-xl font-semibold text-sm border-2 border-foreground-900 text-foreground-900 whitespace-nowrap"
                onClick={closeAll}
              >
                Pandabuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 text-center px-4 py-2.5 rounded-xl font-semibold text-sm bg-primary-500 text-background-50 whitespace-nowrap"
                onClick={closeAll}
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}